import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './NotificationModal.css';
import moment from 'moment';
import Swal from 'sweetalert2';
import AddTodoModal from './AddTodoModal';
import todoService from '../api/todoService';

Modal.setAppElement('#root');

function NotificationModal({ isOpen, onRequestClose }) {
  const [allTodos, setAllTodos] = useState([]);
  const [isAddTodoModalOpen, setIsAddTodoModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState('todos'); // 'todos' or 'reservations'

  const fetchTodos = async () => {
    try {
      const response = await todoService.getTodos();
      const sortedTodos = response.data.sort((a, b) => moment(a.date).diff(moment(b.date)));
      setAllTodos(sortedTodos);
    } catch (error) {
      console.error("Failed to fetch todos:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTodos();
    }
  }, [isOpen]);

  const handleDateClick = (date) => {
    setSelectedDate(moment(date).format('YYYY-MM-DD'));
    setIsAddTodoModalOpen(true);
  };

  const handleAddTodo = async (todoText) => {
    try {
      const type = activeTab === 'reservations' ? 'reservation' : 'todo';

      if (type === 'reservation') {
        const today = moment().startOf('day');
        const selected = moment(selectedDate);
        if (!selected.isAfter(today)) {
          Swal.fire({
            icon: 'error',
            title: '예약 불가',
            text: '예약은 오늘 이후의 날짜에만 생성할 수 있습니다.',
          });
          return;
        }
      }

      const newTodo = {
        date: selectedDate,
        text: todoText,
        checked: false,
        type: type,
      };
      await todoService.createTodo(newTodo);
      fetchTodos();
    } catch (error) {
      console.error("Failed to add todo:", error);
    }
  };

  const handleToggleTodo = async (id, todo) => {
    try {
      const updatedTodo = { ...todo, checked: !todo.checked };
      await todoService.updateTodo(id, updatedTodo);
      fetchTodos();
    } catch (error) {
      console.error("Failed to toggle todo:", error);
    }
  };

  const handleDeleteTodo = async (id) => {
    try {
      await todoService.deleteTodo(id);
      fetchTodos();
    } catch (error) {
      console.error("Failed to delete todo:", error);
    }
  };

  const handleDeleteCheckedTodos = async () => {
    try {
      const listToDelete = activeTab === 'todos' ? regularTodos : reservationTodos;
      const checkedIds = listToDelete.filter(t => t.checked).map(t => t.id);
      
      if (checkedIds.length === 0) return;

      await Promise.all(checkedIds.map(id => todoService.deleteTodo(id)));
      fetchTodos();
    } catch (error) {
      console.error("Failed to delete checked todos:", error);
    }
  };

  const regularTodos = allTodos.filter(t => t.type === 'todo');
  const reservationTodos = allTodos.filter(t => t.type === 'reservation');

  const currentList = activeTab === 'todos' ? regularTodos : reservationTodos;
  const allCurrentTodosChecked = currentList.length > 0 && currentList.every(todo => todo.checked);

  const handleToggleAllTodos = async (e) => {
    const checked = e.target.checked;
    const idsToToggle = currentList.map(t => t.id);
    if (idsToToggle.length === 0) return;
    try {
      // This assumes a backend endpoint that can toggle multiple todos by their IDs.
      // If not available, this needs to be implemented as sequential requests.
      await todoService.toggleAllTodos(idsToToggle, checked, e);
      fetchTodos();
    } catch (error) {
      console.error("Failed to toggle all todos:", error);
    }
  };

  const TodoList = ({ todos }) => (
    <ul className="todo-list">
      <li className="select-all-item">
        <label className="toggle-all-checkbox-label">
          <input
            type="checkbox"
            checked={allCurrentTodosChecked}
            onChange={handleToggleAllTodos}
            disabled={todos.length === 0}
          />
          {'\u00A0'}{'\u00A0'}전체 선택
        </label>
      </li>
      {todos.map((todo) => (
        <li key={todo.id} className={todo.checked ? 'checked' : ''}>
          <input
            type="checkbox"
            checked={todo.checked}
            onChange={() => handleToggleTodo(todo.id, todo)}
          />
          <span className="todo-date">{todo.date}</span>
          <span className="todo-text">{todo.text}</span>
          <button onClick={() => handleDeleteTodo(todo.id)}>삭제</button>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        className="notification-modal"
        overlayClassName="notification-overlay"
      >
        <h2>개인 일정 관리</h2>
        <div className="notification-content">
          <div className="calendar-container">
            <Calendar
              onClickDay={handleDateClick}
              formatDay={(locale, date) => moment(date).format('D')}
            />
          </div>
          <div className="todo-list-container">
            <div className="tabs">
              <button 
                className={`tab-button ${activeTab === 'todos' ? 'active' : ''}`}
                onClick={() => setActiveTab('todos')}
              >
                {activeTab === 'todos' && <span style={{ color: 'green' }}>✔️ </span>}할일 목록 ({regularTodos.length})
              </button>
              <button 
                className={`tab-button ${activeTab === 'reservations' ? 'active' : ''}`}
                onClick={() => setActiveTab('reservations')}
              >
                {activeTab === 'reservations' && <span style={{ color: 'green' }}>✔️ </span>}예약 목록 ({reservationTodos.length})
              </button>
            </div>
            <div className="todo-list-header">
              <h3>{activeTab === 'todos' ? '할일 목록' : '예약 목록'}</h3>
              <div className="todo-actions">
                <button onClick={handleDeleteCheckedTodos} className="delete-checked-button">
                  완료 목록 삭제
                </button>
              </div>
            </div>
            {activeTab === 'todos' ? <TodoList todos={regularTodos} /> : <TodoList todos={reservationTodos} />}
          </div>
        </div>
        <button onClick={onRequestClose} className="close-button">닫기</button>
      </Modal>
      <AddTodoModal
        isOpen={isAddTodoModalOpen}
        onRequestClose={() => setIsAddTodoModalOpen(false)}
        onAddTodo={handleAddTodo}
        selectedDate={selectedDate}
      />
    </>
  );
}

export default NotificationModal;
